'use client'

import { gql, useLazyQuery, useMutation } from '@apollo/client'
import axios from 'axios'
import { useSnackbar } from 'notistack'
import { ReactNode, createContext, useContext, useReducer } from 'react'
import { v4 as uuidv4 } from 'uuid'

import { graphql } from '@core/shared/gql'

import { getExtension } from '../(dashboard)/videos/[videoId]/audio/add/_utils/getExtension'
import { refreshToken } from '../../app/api'

export const CREATE_MUX_VIDEO_UPLOAD_BY_URL = graphql(`
  mutation CreateMuxVideoUploadByUrl(
    $url: String!
    $userGenerated: Boolean
    $downloadable: Boolean
    $maxResolution: MaxResolutionTier
  ) {
    createMuxVideoUploadByUrl(
      url: $url
      userGenerated: $userGenerated
      downloadable: $downloadable
      maxResolution: $maxResolution
    ) {
      id
      assetId
      playbackId
      uploadUrl
      readyToStream
    }
  }
`)

export const CREATE_VIDEO_VARIANT = graphql(`
  mutation CreateVideoVariant($input: VideoVariantCreateInput!) {
    videoVariantCreate(input: $input) {
      id
      videoId
      slug
      hls
      published
      language {
        id
        slug
        name {
          value
          primary
        }
      }
    }
  }
`)

export const GET_MY_MUX_VIDEO = graphql(`
  query GetMyMuxVideo($id: ID!, $userGenerated: Boolean) {
    getMyMuxVideo(id: $id, userGenerated: $userGenerated) {
      id
      assetId
      playbackId
      readyToStream
      duration
    }
  }
`)

export const PREPARE_R2_MULTIPART = gql`
  mutation PrepareCloudflareR2Multipart(
    $input: CloudflareR2MultipartPrepareInput!
  ) {
    cloudflareR2MultipartPrepare(input: $input) {
      id
      uploadId
      fileName
      publicUrl
      partSize
      parts {
        partNumber
        uploadUrl
      }
    }
  }
`

export const COMPLETE_R2_MULTIPART = gql`
  mutation CloudflareR2CompleteMultipart(
    $input: CloudflareR2CompleteMultipartInput!
  ) {
    cloudflareR2CompleteMultipart(input: $input) {
      id
      fileName
      publicUrl
    }
  }
`

interface UploadVideoVariantState {
  isUploading: boolean
  uploadProgress: number
  uploadedBytes: number
  totalBytes: number
  uploadSpeedBps: number | null
  etaSeconds: number | null
  isProcessing: boolean
  error: string | null
  muxVideoId: string | null
  edition: string | null
  languageId: string | null
  languageSlug: string | null
  videoId: string | null
  videoSlug: string | null
  published: boolean | null
  onComplete?: () => void
}

interface UploadVideoVariantContextType {
  uploadState: UploadVideoVariantState
  startUpload: (
    file: File,
    videoId: string,
    languageId: string,
    languageSlug: string,
    edition: string,
    published: boolean,
    videoSlug?: string,
    onComplete?: () => void
  ) => Promise<void>
  clearUploadState: () => void
}

const initialState: UploadVideoVariantState = {
  isUploading: false,
  uploadProgress: 0,
  uploadedBytes: 0,
  totalBytes: 0,
  uploadSpeedBps: null,
  etaSeconds: null,
  isProcessing: false,
  error: null,
  muxVideoId: null,
  edition: null,
  languageId: null,
  languageSlug: null,
  videoId: null,
  videoSlug: null,
  published: null,
  onComplete: undefined
}

const UploadVideoVariantContext = createContext<
  UploadVideoVariantContextType | undefined
>(undefined)

const MIN_MULTIPART_PART_SIZE = 5 * 1024 * 1024
const DEFAULT_MULTIPART_PART_SIZE = 64 * 1024 * 1024
const MAX_MULTIPART_PARTS = 10000

function calculateMultipartPartSize(fileSize: number): number {
  if (fileSize <= DEFAULT_MULTIPART_PART_SIZE) {
    return Math.max(fileSize, MIN_MULTIPART_PART_SIZE)
  }

  const partsWithDefault = Math.ceil(fileSize / DEFAULT_MULTIPART_PART_SIZE)
  if (partsWithDefault <= MAX_MULTIPART_PARTS)
    return DEFAULT_MULTIPART_PART_SIZE

  const sizedForMaxParts = Math.ceil(fileSize / MAX_MULTIPART_PARTS)
  return Math.max(MIN_MULTIPART_PART_SIZE, sizedForMaxParts)
}

type UploadAction =
  | {
      type: 'START_UPLOAD'
      videoId: string
      languageId: string
      languageSlug: string
      edition: string
      published: boolean
      onComplete?: () => void
      videoSlug?: string
      totalBytes: number
    }
  | {
      type: 'SET_PROGRESS'
      progress: number
      uploadedBytes?: number
      uploadSpeedBps?: number | null
      etaSeconds?: number | null
    }
  | { type: 'START_PROCESSING'; muxVideoId: string }
  | { type: 'COMPLETE' }
  | { type: 'SET_ERROR'; error: string }
  | { type: 'CLEAR' }

function uploadReducer(
  state: UploadVideoVariantState,
  action: UploadAction
): UploadVideoVariantState {
  switch (action.type) {
    case 'START_UPLOAD':
      return {
        ...initialState,
        isUploading: true,
        uploadedBytes: 0,
        totalBytes: action.totalBytes,
        uploadSpeedBps: null,
        etaSeconds: null,
        videoId: action.videoId,
        languageId: action.languageId,
        languageSlug: action.languageSlug,
        edition: action.edition,
        published: action.published,
        onComplete: action.onComplete,
        videoSlug: action.videoSlug ?? null
      }
    case 'SET_PROGRESS':
      return {
        ...state,
        uploadProgress: action.progress,
        uploadedBytes: action.uploadedBytes ?? state.uploadedBytes,
        uploadSpeedBps:
          action.uploadSpeedBps !== undefined
            ? action.uploadSpeedBps
            : state.uploadSpeedBps,
        etaSeconds:
          action.etaSeconds !== undefined ? action.etaSeconds : state.etaSeconds
      }
    case 'START_PROCESSING':
      return {
        ...state,
        isUploading: false,
        isProcessing: true,
        muxVideoId: action.muxVideoId
      }
    case 'COMPLETE':
      return initialState
    case 'SET_ERROR':
      return { ...initialState, error: action.error }
    case 'CLEAR':
      return { ...state, error: null }
    default:
      return state
  }
}

export function UploadVideoVariantProvider({
  children
}: {
  children: ReactNode
}) {
  const [state, dispatch] = useReducer(uploadReducer, initialState)
  const { enqueueSnackbar } = useSnackbar()

  const [prepareR2Multipart] = useMutation(PREPARE_R2_MULTIPART)
  const [completeR2Multipart] = useMutation(COMPLETE_R2_MULTIPART)
  const [createMuxVideo] = useMutation(CREATE_MUX_VIDEO_UPLOAD_BY_URL)
  const [createVideoVariant] = useMutation(CREATE_VIDEO_VARIANT)
  const [getMyMuxVideo, { stopPolling }] = useLazyQuery(GET_MY_MUX_VIDEO, {
    pollInterval: 1000,
    notifyOnNetworkStatusChange: true,
    onCompleted: async (data) => {
      if (
        data.getMyMuxVideo.playbackId != null &&
        data.getMyMuxVideo.assetId != null &&
        data.getMyMuxVideo.readyToStream &&
        state.muxVideoId != null
      ) {
        stopPolling()
        await handleCreateVideoVariant(
          data.getMyMuxVideo.id,
          data.getMyMuxVideo.playbackId,
          data.getMyMuxVideo.duration
        )
      }
    },
    onError: (error) => {
      stopPolling()
      const errorMessage = error.message || 'Failed to get Mux video status'
      dispatch({ type: 'SET_ERROR', error: errorMessage })
      enqueueSnackbar(errorMessage, { variant: 'error' })
    }
  })

  const handleCreateVideoVariant = async (
    muxId: string,
    playbackId: string,
    duration?: number | null
  ) => {
    if (
      state.videoId == null ||
      state.languageId == null ||
      state.languageSlug == null ||
      state.edition == null ||
      state.videoSlug == null
    )
      return

    // Calculate lengthInMilliseconds from duration (duration is in seconds)
    const durationInSeconds = duration ?? 0
    const lengthInMilliseconds = durationInSeconds * 1000

    try {
      await createVideoVariant({
        variables: {
          input: {
            id: `${state.languageId}_${state.videoId}`,
            videoId: state.videoId,
            edition: state.edition,
            languageId: state.languageId,
            slug: `${state.videoSlug}/${state.languageSlug}`,
            downloadable: true,
            published: state.published ?? false,
            muxVideoId: muxId,
            hls: `https://stream.mux.com/${playbackId}.m3u8`,
            duration: durationInSeconds,
            lengthInMilliseconds: lengthInMilliseconds
          }
        },
        onCompleted: () => {
          state.onComplete?.()
        },
        update: (cache, { data }) => {
          if (data?.videoVariantCreate == null || state.videoId == null) return
          cache.modify({
            id: cache.identify({
              __typename: 'Video',
              id: state.videoId
            }),
            fields: {
              variants(existingVariants = []) {
                const newVariantRef = cache.writeFragment({
                  data: data.videoVariantCreate,
                  fragment: graphql(`
                    fragment NewVariant on VideoVariant {
                      id
                      videoId
                      slug
                      hls
                      language {
                        id
                        slug
                        name {
                          value
                          primary
                        }
                      }
                    }
                  `)
                })
                return [...existingVariants, newVariantRef]
              }
            }
          })
        }
      })

      dispatch({ type: 'COMPLETE' })
      enqueueSnackbar('Audio Language Added', { variant: 'success' })
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Processing failed'
      dispatch({ type: 'SET_ERROR', error: errorMessage })
      enqueueSnackbar(errorMessage, { variant: 'error' })
    }
  }

  const startUpload = async (
    file: File,
    videoId: string,
    languageId: string,
    languageSlug: string,
    edition: string,
    published: boolean,
    videoSlug?: string,
    onComplete?: () => void
  ) => {
    try {
      dispatch({
        type: 'START_UPLOAD',
        videoId,
        languageId,
        languageSlug,
        edition,
        published,
        onComplete,
        videoSlug,
        totalBytes: file.size
      })

      const videoVariantId = `${languageId}_${videoId}`
      const extension = getExtension(file.name)
      const fileName = `${videoId}/variants/${languageId}/videos/${uuidv4()}/${videoVariantId}${extension}`
      const r2Response = await prepareR2Multipart({
        variables: {
          input: {
            fileName,
            contentType: file.type,
            originalFilename: file.name,
            contentLength: file.size,
            videoId
          }
        }
      })

      const multipartData = r2Response.data?.cloudflareR2MultipartPrepare

      if (
        multipartData?.uploadId == null ||
        multipartData?.publicUrl == null ||
        multipartData?.parts == null
      ) {
        const errorMessage = 'Failed to prepare R2 multipart upload'
        dispatch({ type: 'SET_ERROR', error: errorMessage })
        enqueueSnackbar(errorMessage, { variant: 'error' })
        return
      }

      const partSize =
        multipartData.partSize ?? calculateMultipartPartSize(file.size)
      const parts = multipartData.parts

      if (parts.length === 0) {
        const errorMessage = 'Failed to prepare R2 multipart upload'
        dispatch({ type: 'SET_ERROR', error: errorMessage })
        enqueueSnackbar(errorMessage, { variant: 'error' })
        return
      }

      const abortController = new AbortController()
      const keepAliveInterval = setInterval(() => {
        void refreshToken().catch(() => undefined)
      }, 45000)

      const uploadedParts: Array<{ partNumber: number; eTag: string }> = []
      const totalSize = file.size
      let uploadedBytes = 0
      let lastProgressTime = Date.now()
      let lastProgressUploaded = 0

      try {
        const sortedParts = [...parts].sort(
          (first, second) => first.partNumber - second.partNumber
        )

        for (const part of sortedParts) {
          const start = (part.partNumber - 1) * partSize
          if (start >= file.size) break

          const end = Math.min(start + partSize, file.size)
          const chunk = file.slice(start, end)

          if (part.uploadUrl == null) {
            throw new Error(`Missing upload URL for part ${part.partNumber}`)
          }

          const uploadResult = await axios.put(part.uploadUrl, chunk, {
            headers: {
              'Content-Type': file.type
            },
            signal: abortController.signal as unknown as AbortSignal,
            onUploadProgress: (progressEvent) => {
              const loaded = progressEvent.loaded ?? 0
              const totalUploaded = uploadedBytes + loaded
              const percentCompleted = Math.round(
                (totalUploaded * 100) / totalSize
              )

              const now = Date.now()
              const deltaBytes = totalUploaded - lastProgressUploaded
              const deltaTimeMs = now - lastProgressTime
              const speedBps =
                deltaTimeMs > 0 ? (deltaBytes * 1000) / deltaTimeMs : null
              const etaSeconds =
                speedBps != null && speedBps > 0
                  ? Math.max(0, (totalSize - totalUploaded) / speedBps)
                  : null

              dispatch({
                type: 'SET_PROGRESS',
                progress: percentCompleted,
                uploadedBytes: totalUploaded,
                uploadSpeedBps: speedBps,
                etaSeconds
              })

              lastProgressUploaded = totalUploaded
              lastProgressTime = now
            }
          })

          const headers = uploadResult.headers as Record<
            string,
            string | string[] | undefined
          > &
            Partial<{ get: (name: string) => string | null }>

          const eTagHeader =
            headers?.etag ??
            headers?.ETag ??
            headers?.ETAG ??
            headers?.['ETag'] ??
            headers?.get?.('etag') ??
            headers?.get?.('ETag')

          if (eTagHeader == null) {
            const availableHeaders = headers
              ? Object.keys(headers).join(', ')
              : 'none'
            throw new Error(
              `Missing ETag for part ${part.partNumber}. Available headers: ${availableHeaders}`
            )
          }

          uploadedBytes += chunk.size
          dispatch({
            type: 'SET_PROGRESS',
            progress: Math.round((uploadedBytes * 100) / totalSize),
            uploadedBytes
          })

          uploadedParts.push({
            partNumber: part.partNumber,
            eTag: Array.isArray(eTagHeader)
              ? eTagHeader[0].replace(/"/g, '')
              : eTagHeader.replace(/"/g, '')
          })
        }
      } finally {
        clearInterval(keepAliveInterval)
        abortController.abort()
      }

      if (uploadedParts.length === 0) {
        throw new Error('No parts uploaded to R2')
      }

      await completeR2Multipart({
        variables: {
          input: {
            id: multipartData.id,
            fileName: multipartData.fileName,
            uploadId: multipartData.uploadId,
            parts: uploadedParts
          }
        }
      })

      const muxResponse = await createMuxVideo({
        variables: {
          url: multipartData.publicUrl,
          userGenerated: false,
          downloadable: true,
          maxResolution: 'uhd'
        }
      })

      if (muxResponse.data?.createMuxVideoUploadByUrl?.id == null) {
        const errorMessage = 'Failed to create Mux video'
        dispatch({ type: 'SET_ERROR', error: errorMessage })
        enqueueSnackbar(errorMessage, { variant: 'error' })
        return
      }

      dispatch({
        type: 'START_PROCESSING',
        muxVideoId: muxResponse.data.createMuxVideoUploadByUrl.id
      })

      // Start polling for Mux video status
      void getMyMuxVideo({
        variables: {
          id: muxResponse.data.createMuxVideoUploadByUrl.id,
          userGenerated: false
        }
      })
    } catch (error) {
      let errorMessage: string

      if (error instanceof Error) {
        errorMessage = error.message || 'Failed to upload video'
      } else {
        errorMessage = 'Failed to upload video'
      }

      dispatch({ type: 'SET_ERROR', error: errorMessage })
      enqueueSnackbar(errorMessage, { variant: 'error' })
    }
  }

  const clearUploadState = () => {
    dispatch({ type: 'CLEAR' })
  }

  return (
    <UploadVideoVariantContext.Provider
      value={{
        uploadState: state,
        startUpload,
        clearUploadState
      }}
    >
      {children}
    </UploadVideoVariantContext.Provider>
  )
}

export function useUploadVideoVariant() {
  const context = useContext(UploadVideoVariantContext)
  if (context === undefined) {
    throw new Error(
      'useUploadVideoVariant must be used within a UploadVideoVariantProvider'
    )
  }
  return context
}
